import { useEffect, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { Authenticating } from 'components/Authenticating';
import { AuthenticationComplete } from 'components/AuthenticationComplete';
import { SessionLost } from 'components/SessionLost';
import { stripCallbackFromUrl, consumePostLoginRedirect } from 'utils/url';

// The IdP redirects back to /authentication/callback?code=...&state=...
// That path is NOT a declared route, so if the router is rendered while it is on
// the URL, the catch-all <Route path='*'><NotFound /></Route> wins and the user
// sees "Oops! Page not found" instead of the callback screen.
//
// This component owns that window. It NEVER calls signinRedirect(): the app is
// public/browsable while anonymous and only `secure` routes prompt for login.

export function AuthGate({ children }) {
  const auth = useAuth();

  // True -> "the browser URL is still `/authentication/callback`__
  // re-checked on every auth state change (never mutated during render).
  const [isOnCallbackUrl, setIsOnCallbackUrl] = useState(() => hasAuthParams());

  useEffect(() => {
    if (!isOnCallbackUrl) return undefined;

    const waitForExchange = () => {

      if (!hasAuthParams()) // URL got cleaned -> exchange is done
      {
        // Rewrite the URL AND notify React Router before the router is rendered
        // again, otherwise it keeps seeing /authentication/callback and never
        // matches the route the user originally requested.
        stripCallbackFromUrl(consumePostLoginRedirect());
        setIsOnCallbackUrl(false);
      }
    };

    waitForExchange();
    const timer = setInterval(waitForExchange, 100);
    return () => clearInterval(timer);
  }, [isOnCallbackUrl]);

  // Keep rendering the callback screen while we are on the callback URL and no failure has been reported.
  if (isOnCallbackUrl && !auth.error) {
    return <AuthenticationComplete />;
  }

  if (auth.error) {
    return <SessionLost />;
  }

  // Silent renew / redirect in flight: same full-screen treatment as before.
  if (auth.activeNavigator === 'signinSilent' || auth.activeNavigator === 'signinRedirect') {
    return <Authenticating />;
  }

  return children;
}

export default AuthGate;
